<template>
  <Card>
    <CardContent class="tw-pt-4">
      <CardTitle class="tw-uppercase tw-text-[0.8rem] tw-mb-1">
        {{ t('profile.titles.customer_signatures') }}
      </CardTitle>
      <p class="tw-text-xs tw-text-secondary-500 tw-mb-3">
        {{ t('profile.titles.customer_signatures_hint') }}
      </p>

      <!-- shared hidden file input reused for add/replace -->
      <input
        ref="fileInput"
        type="file"
        class="tw-hidden"
        accept="image/png, image/jpeg"
        @change="onFileSelected"
      />

      <!-- add a signature for a procedure without one yet -->
      <div
        v-if="isAdmin || availableCustomers.length"
        class="tw-flex tw-gap-2 tw-items-center tw-mb-4"
      >
        <SearchableSelect
          class="tw-flex-1"
          :model-value="selectedCustomerId"
          :options="pickerOptions"
          :remote-search="isAdmin"
          :loading="customerLoading"
          :has-more="customerHasMore"
          value-prop="customerId"
          label-prop="customerName"
          :placeholder="t('profile.titles.select_customer')"
          @update:model-value="onPickCustomer"
          @search-change="onCustomerSearch"
          @load-more="customerLoadMore"
        />
        <Button
          variant="secondary"
          class="tw-text-xs"
          :disabled="selectedCustomerId === null"
          @click="triggerUpload(selectedCustomerId!)"
        >
          <i class="icon-upload"></i>
          {{ t('profile.titles.add_customer_signature') }}
        </Button>
      </div>

      <!-- configured per-customer signatures -->
      <div v-if="customerSignatures.length" class="tw-flex tw-flex-col tw-gap-3">
        <div
          v-for="sig in customerSignatures"
          :key="sig.customer"
          class="tw-flex tw-items-center tw-gap-3 tw-border tw-border-secondary-200 tw-rounded-md tw-p-2"
        >
          <span class="tw-flex-1 tw-text-sm tw-font-medium tw-text-secondary-600">
            {{ sig.customer_name }}
          </span>
          <img
            :src="sig.signature"
            :alt="sig.customer_name"
            class="tw-h-12 tw-w-24 tw-object-contain"
          />
          <Button variant="secondary" class="tw-text-xs" @click="triggerUpload(sig.customer)">
            {{ t('profile.titles.replace') }}
          </Button>
          <Button
            variant="ghost"
            class="tw-text-xs tw-text-red-500"
            @click="onDelete(sig.customer)"
          >
            {{ t('profile.titles.delete') }}
          </Button>
        </div>
      </div>
      <p v-else class="tw-text-sm tw-text-secondary-400">
        {{ t('profile.titles.no_customer_signatures') }}
      </p>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { useUserStore } from '@/stores/user.store';
import { useFileValidation } from '@/_helpers/composables/file-validation.composable';
import { useCustomerSelect } from '@/composables/useCustomerSelect';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useI18n } from 'vue-i18n';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';

const { t } = useI18n();
const toast = useToast();

const { currentUser } = storeToRefs(useUserStore());
const { getAssignedCustomersWithRolesList, uploadCustomerSignature, deleteCustomerSignature } =
  useUserStore();

// admins have no customer_assignments but implicitly access all procedures, so
// their picker is server-side searched/paginated over the full customer list
const {
  customers: adminCustomers,
  loading: customerLoading,
  hasMore: customerHasMore,
  fetchCustomers,
  loadMore: customerLoadMore,
  onSearch: customerOnSearch,
} = useCustomerSelect();

const isAdmin = computed(() => !!currentUser.value?.is_admin);

const fileInput: Ref<HTMLInputElement | null> = ref(null);
const selectedCustomerId: Ref<number | null> = ref(null);
// the procedure the next file selection applies to (add or replace)
const uploadTargetCustomerId: Ref<number | null> = ref(null);

const customerSignatures = computed(() => currentUser.value?.customer_signatures ?? []);
const configuredIds = computed(() => new Set(customerSignatures.value.map(s => s.customer)));

// non-admin picker source: the user's assigned active procedures (client-side)
const availableCustomers = computed(() => {
  if (!currentUser.value || isAdmin.value) return [];
  return getAssignedCustomersWithRolesList(currentUser.value)
    .filter(c => c.isCustomerActive)
    .filter(c => !configuredIds.value.has(c.customerId));
});

// unified {customerId, customerName} options for the picker, minus already-configured
const pickerOptions = computed(() => {
  if (isAdmin.value) {
    return adminCustomers.value
      .filter(c => !configuredIds.value.has(c.id))
      .map(c => ({ customerId: c.id, customerName: c.name }));
  }
  return availableCustomers.value.map(c => ({
    customerId: c.customerId,
    customerName: c.customerName,
  }));
});

const onPickCustomer = (value: number | string | null) => {
  selectedCustomerId.value = value == null ? null : Number(value);
};

const onCustomerSearch = (query: string) => {
  if (isAdmin.value) customerOnSearch(query);
};

const triggerUpload = (customerId: number) => {
  uploadTargetCustomerId.value = customerId;
  fileInput.value?.click();
};

const onFileSelected = async (e: any) => {
  const file: File = e.target.files[0];
  // allow selecting the same file again later
  e.target.value = '';

  if (!file || uploadTargetCustomerId.value === null) return;

  const { validateSize, validateTypeAndExtension } = useFileValidation();
  if (!(validateSize(file, 0, 10) && validateTypeAndExtension(file))) return;

  try {
    await uploadCustomerSignature(uploadTargetCustomerId.value, file);
    selectedCustomerId.value = null;
    toast.open({ message: t('profile.titles.customer_signature_saved'), type: 'success' });
  } catch (error) {
    toast.open({ message: t('profile.titles.customer_signature_failed'), type: 'error' });
  } finally {
    uploadTargetCustomerId.value = null;
  }
};

const onDelete = async (customerId: number) => {
  try {
    await deleteCustomerSignature(customerId);
    toast.open({ message: t('profile.titles.customer_signature_removed'), type: 'success' });
  } catch (error) {
    toast.open({ message: t('profile.titles.customer_signature_failed'), type: 'error' });
  }
};

onMounted(() => {
  // seed the admin picker with the first page of active customers
  if (isAdmin.value) fetchCustomers();
});
</script>

<style scoped>
/* Using Tailwind classes - no additional styles needed */
</style>
